import { useEffect, useMemo, useRef } from 'react';
import { useLatestRef } from './useLatestRef';

export interface CallOncePerAnimationFrame<TValue> {
  /** Calls the callback in the next frame with the latest value of this frame. */
  call: (value: TValue) => void;
  /** Calls it now with the waiting value, if any (e.g. before a menu closes). */
  flush: () => void;
  /** Drops the waiting value (e.g. another choice replaced it). */
  cancel: () => void;
}

/**
 * Calls `callback` at most once per animation frame, with the latest
 * argument: e.g. a colour picker dragged across its area fires on every
 * pointer move, while one change per frame is all the user can see.
 */
export function useCallOncePerAnimationFrame<TValue>(
  callback: (value: TValue) => void
): CallOncePerAnimationFrame<TValue> {
  const callbackRef = useLatestRef(callback);
  const latestValueRef = useRef<{ value: TValue } | null>(null);
  const frameRef = useRef<number | null>(null);

  const scheduler = useMemo(() => {
    const cancel = (): void => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      latestValueRef.current = null;
    };
    const flush = (): void => {
      const latest = latestValueRef.current;
      cancel();
      if (latest) {
        callbackRef.current(latest.value);
      }
    };
    const call = (value: TValue): void => {
      latestValueRef.current = { value };
      if (frameRef.current === null) {
        frameRef.current = requestAnimationFrame(flush);
      }
    };
    return { call, flush, cancel };
  }, [callbackRef]);

  useEffect(() => scheduler.cancel, [scheduler]);

  return scheduler;
}
